import { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { cn } from '@/lib/utils';
import { Switch } from '@/components/ui/switch';
import { supabase } from '@/integrations/supabase/client';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useSessionManager } from '@/hooks/useSessionManager';
import { useToast } from '@/hooks/use-toast';
import {
  LayoutDashboard,
  Users,
  Building2,
  TrendingUp,
  Wallet,
  Landmark,
  FileText,
  Settings,
  Moon,
  LogOut,
  ChevronRight,
  ChevronLeft,
  Pin,
  User,
  Menu,
  X,
  Loader2,
} from 'lucide-react';

// Items de navigation principale
const NAV_ITEMS = [
  { icon: LayoutDashboard, label: 'Tableau de bord', path: '/dashboard' },
  { icon: Users, label: 'Équipe', path: '/team' },
  { icon: Building2, label: 'Chantiers', path: '/projects' },
  { icon: TrendingUp, label: 'Rentabilité', path: '/rentabilite' },
  { icon: Wallet, label: 'Gestion financière', path: '/financial' },
  { icon: Landmark, label: 'Trésorerie', path: '/tresorerie' },
  { icon: FileText, label: 'Rapports', path: '/reports' },
];

interface SidebarProps {
  className?: string;
}

export function Sidebar({ className }: SidebarProps) {
  const [isPinned, setIsPinned] = useState(() => {
    // Charger l'état épinglé depuis localStorage (par défaut: épinglé/déplié)
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('b8ild_sidebar_pinned');
      // Si jamais défini, épinglé par défaut (true)
      return stored === null ? true : stored === 'true';
    }
    return true; // Par défaut épinglé
  });
  const [isHovering, setIsHovering] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [user, setUser] = useState<any>(null);

  const { theme, setTheme } = useTheme();
  const { logout } = useSessionManager();
  const { toast } = useToast();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
    } finally {
      setIsLoggingOut(false);
    }
  };

  // La sidebar est dépliée si épinglée OU si hover (desktop) OU ouverte (mobile)
  const isOpen = isMobile ? isMobileOpen : (isPinned || isHovering);

  // Charger les infos utilisateur
  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
    };
    getUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Sauvegarder l'état épinglé
  useEffect(() => {
    localStorage.setItem('b8ild_sidebar_pinned', isPinned.toString());
  }, [isPinned]);

  // Fermer le menu mobile quand on navigue
  useEffect(() => {
    if (isMobile) {
      setIsMobileOpen(false);
    }
  }, [isMobile]);

  const handleMouseEnter = () => {
    if (!isPinned && !isMobile) {
      setIsHovering(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isPinned && !isMobile) {
      setIsHovering(false);
    }
  };

  const handleTogglePin = () => {
    setIsPinned(!isPinned);
    if (!isPinned) {
      setIsHovering(false);
    }
  };

  const handleExpand = () => {
    if (isMobile) {
      setIsMobileOpen(true);
    } else {
      setIsPinned(true);
    }
  };

  const handleCollapse = () => {
    if (isMobile) {
      setIsMobileOpen(false);
    } else {
      setIsPinned(false);
      setIsHovering(false);
    }
  };

  const toggleTheme = () => {
    // Mode clair temporairement désactivé
    if (theme === 'dark') {
      toast({
        title: "Mode clair bientôt disponible",
        description: "Cette fonctionnalité sera disponible prochainement.",
      });
      return;
    }
    setTheme('dark');
  };

  // Récupérer les infos utilisateur
  const userName = user?.user_metadata?.prenom
    ? `${user.user_metadata.prenom} ${user.user_metadata.nom || ''}`
    : user?.email?.split('@')[0] || 'Utilisateur';
  const userRole = 'Administrateur';

  // Version Mobile : Bouton hamburger + Drawer
  if (isMobile) {
    return (
      <>
        {/* Bouton hamburger fixe */}
        <button
          onClick={() => setIsMobileOpen(true)}
          className="fixed top-4 left-4 z-50 p-2.5 bg-card border border-border rounded-xl shadow-lg hover:bg-accent transition-colors"
          aria-label="Ouvrir le menu"
        >
          <Menu className="w-5 h-5" />
        </button>

        {/* Overlay */}
        {isMobileOpen && (
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
            onClick={() => setIsMobileOpen(false)}
          />
        )}

        {/* Drawer mobile */}
        <aside
          className={cn(
            "fixed left-0 top-0 h-full w-64 bg-card border-r border-border z-50 flex flex-col transition-transform duration-300 ease-in-out shadow-2xl",
            isMobileOpen ? "translate-x-0" : "-translate-x-full"
          )}
        >
          {/* Header avec bouton fermer */}
          <div className="p-4 border-b border-border flex items-center justify-between">
            <div className="flex items-center">
              <span className="text-2xl font-bold tracking-tight">
                B<span className="text-[#F59E0B]">8</span>ild
              </span>
            </div>
            <button
              onClick={() => setIsMobileOpen(false)}
              className="p-2 hover:bg-accent rounded-lg transition-colors"
              aria-label="Fermer le menu"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-3 overflow-y-auto">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 py-2">
              Principal
            </p>
            <div className="space-y-1">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMobileOpen(false)}
                  className={({ isActive }) => cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200",
                    "hover:bg-accent hover:text-accent-foreground",
                    isActive && "bg-sky-500/25 text-sky-400 font-medium"
                  )}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
          </nav>

          {/* Section Système */}
          <div className="p-3 border-t border-border">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 py-2">
              Système
            </p>
            <NavLink
              to="/settings"
              onClick={() => setIsMobileOpen(false)}
              className={({ isActive }) => cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
                "hover:bg-accent hover:text-accent-foreground",
                isActive && "bg-sky-500/25 text-sky-400 font-medium"
              )}
            >
              <Settings className="w-5 h-5 flex-shrink-0" />
              <span>Réglages</span>
            </NavLink>
            <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg">
              <Moon className="w-5 h-5 flex-shrink-0 text-muted-foreground" />
              <span className="flex-1 text-muted-foreground">Dark mode</span>
              <Switch
                checked={theme === 'dark'}
                onCheckedChange={toggleTheme}
                className="data-[state=checked]:bg-primary"
              />
            </div>
          </div>

          {/* Profil */}
          <div className="p-3 border-t border-border">
            <NavLink
              to="/profile"
              onClick={() => setIsMobileOpen(false)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-accent transition-colors"
            >
              <div className="w-9 h-9 bg-muted rounded-full flex items-center justify-center flex-shrink-0">
                <User className="w-4 h-4 text-muted-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{userName}</p>
                <p className="text-xs text-muted-foreground truncate">{userRole}</p>
              </div>
            </NavLink>
          </div>

          {/* Déconnexion */}
          <div className="p-3 border-t border-border">
            <button
              onClick={handleLogout}
              disabled={isLoggingOut}
              className="flex items-center gap-3 px-3 py-2.5 w-full rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground transition-colors disabled:opacity-50"
            >
              {isLoggingOut ? <Loader2 className="w-5 h-5 flex-shrink-0 animate-spin" /> : <LogOut className="w-5 h-5 flex-shrink-0" />}
              <span>Déconnexion</span>
            </button>
          </div>
        </aside>
      </>
    );
  }

  // Version Desktop
  return (
    <>
      {/* Spacer pour compenser la sidebar fixed */}
      <div
        className={cn(
          "h-screen flex-shrink-0 transition-all duration-300 ease-in-out",
          isOpen ? "w-60" : "w-16"
        )}
      />
      <aside
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={cn(
          "fixed left-0 top-0 h-screen bg-card border-r border-border flex flex-col transition-all duration-300 ease-in-out z-40",
          isOpen ? "w-60" : "w-16",
          className
        )}
      >
      {/* Header - Logo + Pin/Expand */}
      <div className="p-3 border-b border-border">
        <div className={cn(
          "flex items-center",
          isOpen ? "justify-between" : "justify-center"
        )}>
          {/* Logo */}
          <div className="flex items-center">
            <span className={cn(
              "font-bold tracking-tight whitespace-nowrap",
              isOpen ? "text-2xl" : "text-lg"
            )}>
              B<span className="text-[#F59E0B]">8</span>{isOpen && "ild"}
            </span>
          </div>

          {/* Boutons Pin / Expand / Collapse */}
          {isOpen ? (
            <div className="flex items-center gap-1">
              <button
                onClick={handleTogglePin}
                className={cn(
                  "p-1.5 rounded-lg transition-colors",
                  isPinned
                    ? "bg-primary/10 text-primary"
                    : "hover:bg-accent text-muted-foreground"
                )}
                title={isPinned ? "Détacher" : "Épingler"}
              >
                <Pin className={cn("w-4 h-4", isPinned && "fill-current")} />
              </button>
              <button
                onClick={handleCollapse}
                className="p-1.5 hover:bg-accent rounded-lg text-muted-foreground transition-colors"
                title="Replier"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={handleExpand}
              className="absolute right-2 top-4 p-1.5 hover:bg-accent rounded-lg text-muted-foreground transition-colors"
              title="Déplier"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Navigation principale */}
      <nav className="flex-1 p-2 overflow-y-auto">
        {/* Label section */}
        {isOpen && (
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 py-2">
            Principal
          </p>
        )}

        <div className="space-y-1">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200",
                "hover:bg-accent hover:text-accent-foreground",
                isActive && "bg-sky-500/25 text-sky-400 font-medium",
                !isOpen && "justify-center"
              )}
              title={!isOpen ? item.label : undefined}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {isOpen && (
                <span className="whitespace-nowrap">{item.label}</span>
              )}
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Section Système */}
      <div className="p-2 border-t border-border">
        {isOpen && (
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 py-2">
            Système
          </p>
        )}

        {/* Réglages */}
        <NavLink
          to="/settings"
          className={({ isActive }) => cn(
            "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
            "hover:bg-accent hover:text-accent-foreground",
            isActive && "bg-sky-500/25 text-sky-400 font-medium",
            !isOpen && "justify-center"
          )}
          title={!isOpen ? "Réglages" : undefined}
        >
          <Settings className="w-5 h-5 flex-shrink-0" />
          {isOpen && <span>Réglages</span>}
        </NavLink>

        {/* Dark Mode Toggle */}
        <div className={cn(
          "flex items-center gap-3 px-3 py-2.5 rounded-lg",
          !isOpen && "justify-center"
        )}>
          <button
            onClick={toggleTheme}
            className="flex items-center gap-3 w-full"
            title={!isOpen ? "Changer le thème" : undefined}
          >
            <Moon className="w-5 h-5 flex-shrink-0 text-muted-foreground" />
            {isOpen && (
              <>
                <span className="flex-1 text-left text-muted-foreground">Dark mode</span>
                <Switch
                  checked={theme === 'dark'}
                  onCheckedChange={toggleTheme}
                  className="data-[state=checked]:bg-primary"
                  onClick={(e) => e.stopPropagation()}
                />
              </>
            )}
          </button>
        </div>
      </div>

      {/* Profil utilisateur */}
      <div className="p-2 border-t border-border">
        <NavLink
          to="/profile"
          className={cn(
            "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
            "hover:bg-accent",
            !isOpen && "justify-center"
          )}
          title={!isOpen ? userName : undefined}
        >
          {/* Avatar */}
          <div className="w-9 h-9 bg-muted rounded-full flex items-center justify-center flex-shrink-0">
            {user?.user_metadata?.avatar_url ? (
              <img
                src={user.user_metadata.avatar_url}
                alt={userName}
                className="w-9 h-9 rounded-full object-cover"
              />
            ) : (
              <User className="w-4 h-4 text-muted-foreground" />
            )}
          </div>

          {isOpen && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{userName}</p>
              <p className="text-xs text-muted-foreground truncate">{userRole}</p>
            </div>
          )}
        </NavLink>
      </div>

      {/* Déconnexion */}
      <div className="p-2 border-t border-border">
        <button
          onClick={handleLogout}
          disabled={isLoggingOut}
          className={cn(
            "flex items-center gap-3 px-3 py-2.5 w-full rounded-lg transition-colors",
            "text-muted-foreground hover:bg-accent hover:text-foreground",
            "disabled:opacity-50",
            !isOpen && "justify-center"
          )}
          title={!isOpen ? "Déconnexion" : undefined}
        >
          {isLoggingOut ? <Loader2 className="w-5 h-5 flex-shrink-0 animate-spin" /> : <LogOut className="w-5 h-5 flex-shrink-0" />}
          {isOpen && <span>Déconnexion</span>}
        </button>
      </div>
    </aside>
    </>
  );
}

export default Sidebar;
